import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare, Users, Zap, TrendingUp, Phone,
  Package, HelpCircle, BarChart3, RefreshCw, AlertCircle,
  ArrowUpRight, CheckCircle2, Clock, ChevronRight,
  Bot, Sparkles, Activity, Globe, Target, Award,
  ArrowUp, ArrowDown, Minus, Bell, Settings,
  Send, Eye, ThumbsUp, Star, Flame, Shield,
  Calendar, MoreHorizontal, ExternalLink, Wifi,
  WifiOff, TrendingDown, MessageCircle, UserCheck,
  DollarSign, ShoppingCart, Layers, Radio, Trophy,
  Percent, CheckSquare, XCircle, AlertTriangle,
  BarChart2, PieChart, Zap as Flash, Smile, Frown,
  Meh, ChevronUp, Hash, Download,CreditCard,
} from 'lucide-react';
import { Switch }                from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api                       from '@/lib/api';
import { toast }                 from 'sonner';
import { useNavigate }           from 'react-router-dom';

const fadeUp = (delay = 0) => ({
  initial:    { opacity: 0, y: 24 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], delay },
});

function AnimatedNumber({ value, duration = 1200, prefix = '', suffix = '' }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    const start = Date.now();
    const to = Number(value) || 0;
    const tick = () => {
      const elapsed  = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease     = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(to * ease));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);
  return <span>{prefix}{display.toLocaleString()}{suffix}</span>;
}

function Sparkline({ data = [], color = '#10b981', height = 40 }) {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const w   = 80;
  const h   = height;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (v / max) * h * 0.85 - 2;
    return `${x},${y}`;
  }).join(' ');
  const lastPt = pts.split(' ').pop().split(',');

  return (
    <svg width={w} height={h} className="overflow-visible">
      <defs>
        <linearGradient id={`sg${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0"    />
        </linearGradient>
      </defs>
      <polyline points={`0,${h} ${pts} ${w},${h}`} fill={`url(#sg${color.replace('#','')})`} stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="3" fill={color} />
    </svg>
  );
}

function TrendBadge({ value }) {
  if (value == null) return null;
  if (value > 0) return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
      <ArrowUp className="w-2.5 h-2.5" />{value}%
    </span>
  );
  if (value < 0) return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">
      <ArrowDown className="w-2.5 h-2.5" />{Math.abs(value)}%
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
      <Minus className="w-2.5 h-2.5" />0%
    </span>
  );
}

function LivePulse({ active = true }) {
  return (
    <span className="relative flex h-2.5 w-2.5">
      {active && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />}
      <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
    </span>
  );
}

function DonutChart({ segments, size = 110 }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
  const r     = 38;
  const cx    = size / 2;
  const cy    = size / 2;
  const circ  = 2 * Math.PI * r;
  let offset  = 0;

  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth="14" />
      {segments.map((seg, i) => {
        const dash = (seg.value / total) * circ;
        const gap  = circ - dash;
        const el   = (
          <circle key={i} cx={cx} cy={cy} r={r}
            fill="none" stroke={seg.color} strokeWidth="14"
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset}
            strokeLinecap="round"
            className="transition-all duration-700"
          />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

function WeeklyBarChart({ data, color = '#10b981' }) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const max   = Math.max(...data, 1);
  const barW  = 24;
  const gap   = 12;
  const chartH = 80;
  const totalW = days.length * (barW + gap) - gap;

  return (
    <div className="w-full">
      <svg width="100%" viewBox={`0 0 ${totalW} ${chartH + 24}`} preserveAspectRatio="xMidYMid meet">
        {data.map((v, i) => {
          const barH = Math.max((v / max) * chartH, 4);
          const x    = i * (barW + gap);
          const y    = chartH - barH;
          const isToday = i === new Date().getDay() - 1;
          return (
            <g key={i}>
              <rect x={x} y={0} width={barW} height={chartH} rx="6" fill="#f3f4f6" />
              <motion.rect
                x={x} y={y} width={barW} height={barH} rx="6"
                fill={isToday ? color : `${color}99`}
                initial={{ height: 0, y: chartH }}
                animate={{ height: barH, y }}
                transition={{ duration: 0.6, delay: i * 0.07, ease: 'easeOut' }}
              />
              <text x={x + barW / 2} y={chartH + 16} textAnchor="middle" fontSize="9" fill={isToday ? color : '#9ca3af'} fontWeight={isToday ? '700' : '400'}>
                {days[i]}
              </text>
              {v > 0 && (
                <text x={x + barW / 2} y={y - 4} textAnchor="middle" fontSize="8" fill={isToday ? color : '#6b7280'} fontWeight="600">
                  {v}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function GoalRing({ value, max, color, size = 60, label, sublabel }) {
  const r    = (size / 2) - 6;
  const circ = 2 * Math.PI * r;
  const pct  = Math.min(value / max, 1);
  const dash = pct * circ;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f3f4f6" strokeWidth="6" />
          <motion.circle
            cx={size/2} cy={size/2} r={r} fill="none"
            stroke={color} strokeWidth="6" strokeLinecap="round"
            strokeDasharray={`${circ}`}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: circ - dash }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold text-gray-800" style={{ color }}>{Math.round(pct * 100)}%</span>
        </div>
      </div>
      <p className="text-xs font-semibold text-gray-700 text-center leading-tight">{label}</p>
      <p className="text-[10px] text-gray-400 text-center">{value}/{max}</p>
    </div>
  );
}

function BotHealthScore({ score }) {
  const getLevel = (s) => {
    if (s >= 80) return { label: 'Excellent', color: '#10b981', icon: Smile, bg: 'bg-emerald-50', text: 'text-emerald-700' };
    if (s >= 60) return { label: 'Good',      color: '#3b82f6', icon: Smile, bg: 'bg-blue-50',    text: 'text-blue-700'    };
    if (s >= 40) return { label: 'Average',   color: '#f59e0b', icon: Meh,   bg: 'bg-amber-50',   text: 'text-amber-700'   };
    return              { label: 'Needs Work', color: '#ef4444', icon: Frown, bg: 'bg-red-50',     text: 'text-red-700'     };
  };
  const level = getLevel(score);
  const circ  = 2 * Math.PI * 40;
  const dash  = (score / 100) * circ;

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24 flex-shrink-0">
        <svg width="96" height="96" className="-rotate-90">
          <circle cx="48" cy="48" r="40" fill="none" stroke="#f3f4f6" strokeWidth="10" />
          <motion.circle
            cx="48" cy="48" r="40" fill="none"
            stroke={level.color} strokeWidth="10" strokeLinecap="round"
            strokeDasharray={`${circ}`}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: circ - dash }}
            transition={{ duration: 1.5, ease: 'easeOut', delay: 0.3 }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold text-gray-900">{score}</span>
          <span className="text-[9px] text-gray-400 -mt-0.5">/100</span>
        </div>
      </div>
      <div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${level.bg} ${level.text} mb-2`}>
          <level.icon className="w-3.5 h-3.5" />
          {level.label}
        </span>
        <p className="text-sm font-semibold text-gray-800">Bot Health Score</p>
        <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
          Based on automation rate, FAQ coverage, response speed & message volume.
        </p>
      </div>
    </div>
  );
}

function ActivityItem({ icon: Icon, color, title, subtitle, time, index }) {
  return (
    <motion.div
      {...fadeUp(0.05 + index * 0.04)}
      className="flex items-start gap-3 py-3 border-b border-gray-50 dark:border-gray-700 last:border-0"
    >
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 leading-snug">{title}</p>
        <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
      </div>
      <span className="text-[11px] text-gray-400 flex-shrink-0 mt-0.5">{time}</span>
    </motion.div>
  );
}

function CheckItem({ done, label, desc, link, navigate }) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl transition-all ${done ? 'opacity-60' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${done ? 'bg-emerald-100' : 'bg-gray-100 dark:bg-gray-700'}`}>
        {done
          ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          : <div className="w-3 h-3 rounded-full border-2 border-gray-300" />
        }
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium leading-snug ${done ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>{label}</p>
        {!done && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
      </div>
      {!done && link && (
        <button onClick={() => navigate(link)} className="text-[11px] text-emerald-600 font-medium hover:underline flex-shrink-0 mt-0.5">
          Fix →
        </button>
      )}
    </div>
  );
}

function buildWeekArray(data = []) {
  const result = [0, 0, 0, 0, 0, 0, 0];
  data.forEach(({ date, count }) => {
    const day = new Date(date).getDay();
    const idx = day === 0 ? 6 : day - 1;
    result[idx] = (result[idx] || 0) + (count || 0);
  });
  return result;
}

export default function Dashboard() {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    overview: {
      total_conversations: 0, active_conversations: 0,
      total_messages: 0, total_products: 0, total_faqs: 0, leads: 0,
      active_products: 0, active_faqs: 0,
    },
    conversations: { active: 0, pending: 0, resolved: 0, unread: 0 },
    messages: { total: 0, by_sender: { customer: 0, bot: 0, agent: 0 } },
    top_performing: null,
  });
  const [settings,   setSettings]   = useState({ is_bot_active: false, welcome_message: '', business_hours: '', location: '' });
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const [greeting,   setGreeting]   = useState('');
  const [now,        setNow]        = useState(new Date());
  const [weeklyData, setWeeklyData] = useState({
    conversations: [0,0,0,0,0,0,0],
    messages:      [0,0,0,0,0,0,0],
    leads:         [0,0,0,0,0,0,0]
  });

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = new Date().getHours();
    if (h < 12) setGreeting('Good morning');
    else if (h < 17) setGreeting('Good afternoon');
    else setGreeting('Good evening');
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(() => fetchDashboardData(true), 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);
      setError(null);

      const [analyticsRes, settingsRes, msgsChartRes, leadsChartRes, convsChartRes] = await Promise.all([
        api.get('/analytics/dashboard'),
        api.get('/business/settings'),
        api.get('/analytics/messages-chart?period=7'),
        api.get('/analytics/leads-chart?period=7'),
        api.get('/analytics/conversations?period=7&group_by=day'),
      ]);

      if (analyticsRes.data.success && analyticsRes.data.dashboard)
        setStats(analyticsRes.data.dashboard);
      if (settingsRes.data.success && settingsRes.data.settings)
        setSettings(settingsRes.data.settings);

      if (msgsChartRes.data.success) {
        setWeeklyData(prev => ({ ...prev, messages: buildWeekArray(msgsChartRes.data.data) }));
      }
      if (leadsChartRes.data.success) {
        setWeeklyData(prev => ({ ...prev, leads: buildWeekArray(leadsChartRes.data.data) }));
      }
      if (convsChartRes.data.success) {
        const raw = convsChartRes.data.data?.time_series || [];
        const mapped = raw.map(d => ({ date: d.period, count: d.total }));
        setWeeklyData(prev => ({ ...prev, conversations: buildWeekArray(mapped) }));
      }

    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
      if (!silent) toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const toggleAutomation = async (enabled) => {
    try {
      await api.put('/business/settings', {
        isBotActive: enabled,
        businessHours: settings.business_hours,
        location: settings.location,
        welcomeMessage: settings.welcome_message,
        fallbackMessage: settings.fallback_message,
      });
      setSettings(s => ({ ...s, is_bot_active: enabled }));
      toast.success(`Automation ${enabled ? 'enabled ✅' : 'disabled'}`);
    } catch {
      toast.error('Failed to update automation');
    }
  };

  const automationRate = stats.messages?.total > 0
    ? ((stats.messages.by_sender?.bot / stats.messages.total) * 100).toFixed(1)
    : 0;

  const responseRate = stats.messages?.total > 0
    ? (((stats.messages.by_sender?.bot + stats.messages.by_sender?.agent) / stats.messages.total) * 100).toFixed(1)
    : 0;

  const resolutionRate = (stats.conversations?.active + stats.conversations?.resolved) > 0
    ? ((stats.conversations.resolved / (stats.conversations.active + stats.conversations.resolved)) * 100).toFixed(0)
    : 0;

  const botHealthScore = Math.min(100, Math.round(
    (Number(automationRate) * 0.35) +
    (Math.min(stats.overview?.total_faqs || 0, 20) / 20 * 100 * 0.25) +
    (Math.min(stats.overview?.total_products || 0, 10) / 10 * 100 * 0.20) +
    (settings.is_bot_active ? 20 : 0)
  ));

  const weeklyConversations = weeklyData.conversations;
  const weeklyMessages      = weeklyData.messages;
  const weeklyLeads         = weeklyData.leads;

  const sparkConv  = [...weeklyData.conversations.slice(0,7), stats.overview?.total_conversations || 0];
  const sparkMsg   = [...weeklyData.messages.slice(0,7), stats.messages?.total || 0];
  const sparkLeads = [...weeklyData.leads.slice(0,7), stats.overview?.leads || 0];

  const donutData = [
    { label: 'Bot',      value: stats.messages?.by_sender?.bot      || 0, color: '#10b981' },
    { label: 'Customer', value: stats.messages?.by_sender?.customer || 0, color: '#3b82f6' },
    { label: 'Agent',    value: stats.messages?.by_sender?.agent    || 0, color: '#8b5cf6' },
  ];

  const goals = [
    { label: 'Conversations', sublabel: 'this month', value: stats.overview?.total_conversations || 0, max: 100, color: '#10b981' },
    { label: 'Leads',         sublabel: 'this month', value: stats.overview?.leads || 0,                max: 50,  color: '#8b5cf6' },
    { label: 'FAQs Trained',  sublabel: 'target 30',  value: stats.overview?.total_faqs || 0,           max: 30,  color: '#f59e0b' },
  ];

  const setupChecklist = [
    { done: settings.is_bot_active,                      label: 'Enable Bot',             desc: 'Turn on your WhatsApp automation',              link: null            },
    { done: (stats.overview?.total_faqs || 0) >= 5,      label: 'Add 5+ FAQs',            desc: 'Train your bot with common customer questions', link: '/app/faqs'     },
    { done: (stats.overview?.total_products || 0) >= 1,  label: 'Add Products/Services',  desc: 'Let customers browse your catalog on WhatsApp', link: '/app/products' },
    { done: !!settings.business_hours,                   label: 'Set Business Hours',     desc: 'Tell the bot when you are available',           link: '/app/settings' },
    { done: !!settings.welcome_message,                  label: 'Set Welcome Message',    desc: 'Greet customers when they first message you',   link: '/app/settings' },
  ];
  const setupDone = setupChecklist.filter(c => c.done).length;

  const activityFeed = [
    { icon: MessageCircle, color: 'bg-blue-50 text-blue-600',     title: 'New conversation started', subtitle: `${stats.conversations?.active || 0} active conversations`,   time: 'Live'  },
    { icon: Bot,           color: 'bg-emerald-50 text-emerald-600', title: 'Bot messages sent',       subtitle: `${stats.messages?.by_sender?.bot || 0} total bot messages`, time: 'Today' },
    { icon: UserCheck,     color: 'bg-violet-50 text-violet-600', title: 'Leads captured',           subtitle: `${stats.overview?.leads || 0} total leads`,                 time: 'Total' },
    { icon: MessageSquare, color: 'bg-rose-50 text-rose-600',     title: 'Unread messages',          subtitle: `${stats.conversations?.unread || 0} need your attention`,    time: 'Now'   },
    { icon: HelpCircle,    color: 'bg-amber-50 text-amber-600',   title: 'FAQs trained',             subtitle: `${stats.overview?.total_faqs || 0} active FAQs`,             time: 'Total' },
    { icon: Package,       color: 'bg-pink-50 text-pink-600',     title: 'Products listed',          subtitle: `${stats.overview?.total_products || 0} products in catalog`, time: 'Total' },
  ];

  if (loading && !stats.overview?.total_conversations) {
    return (
      <div className="h-[calc(100vh-140px)] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-5"
        >
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-100" />
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
            <div className="absolute inset-3 rounded-full bg-emerald-50 flex items-center justify-center">
              <Bot className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-700">Loading dashboard</p>
            <p className="text-xs text-gray-400 mt-0.5">Fetching real-time data…</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6 px-1 pb-8" data-testid="dashboard-page">

      {/* HEADER */}
      <motion.div {...fadeUp(0)} className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <LivePulse active={settings.is_bot_active} />
            <span className="text-xs font-medium text-gray-500">
              {settings.is_bot_active ? 'Bot Active' : 'Bot Offline'}
            </span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-gray-400">
              {now.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
            {greeting} 👋
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400 text-sm">
            Here's what's happening with your WhatsApp automation today.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/app/settings')}
            className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
            aria-label="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => fetchDashboardData()}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50 shadow-sm"
            aria-label="Refresh dashboard"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </motion.div>

      {/* Error */}
      {error && (
        <motion.div {...fadeUp(0.05)}>
          <Alert variant="destructive" className="border-red-200 bg-red-50 text-red-700">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </motion.div>
      )}

      {/* KPI CARDS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            title: 'Conversations',
            value: stats.overview?.total_conversations || 0,
            sub:   `${stats.conversations?.active || 0} active now`,
            icon:  MessageSquare,
            accent: { icon: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'group-hover:ring-emerald-200' },
            trend: 12, spark: sparkConv, color: '#10b981', link: '/app/conversations', delay: 0.08,
          },
          {
            title: 'Messages Sent',
            value: stats.messages?.total || 0,
            sub:   `${stats.messages?.by_sender?.bot || 0} by bot`,
            icon:  Send,
            accent: { icon: 'text-blue-600', bg: 'bg-blue-50', ring: 'group-hover:ring-blue-200' },
            trend: 8, spark: sparkMsg, color: '#3b82f6', link: '/app/conversations', delay: 0.12,
          },
          {
            title: 'Leads Captured',
            value: stats.overview?.leads || 0,
            sub:   'From WhatsApp chats',
            icon:  Target,
            accent: { icon: 'text-violet-600', bg: 'bg-violet-50', ring: 'group-hover:ring-violet-200' },
            trend: 23, spark: sparkLeads, color: '#8b5cf6', link: '/app/leads', delay: 0.16,
          },
          {
            title: 'Unread Messages',
            value: stats.conversations?.unread || 0,
            sub:   `${stats.conversations?.pending || 0} pending reply`,
            icon:  Bell,
            accent: { icon: 'text-orange-600', bg: 'bg-orange-50', ring: 'group-hover:ring-orange-200' },
            trend: stats.conversations?.unread > 0 ? -5 : 0,
            spark: [3,5,4,6,5,7,6,8,7,9,8, stats.conversations?.unread || 0],
            color: '#f97316', link: '/app/conversations', delay: 0.20,
          },
        ].map(card => (
          <motion.div key={card.title} {...fadeUp(card.delay)}>
            <button
              onClick={() => navigate(card.link)}
              className={`group w-full text-left bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-lg hover:border-transparent hover:ring-2 ${card.accent.ring} transition-all duration-300`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-2">{card.title}</span>
                  <div className="text-4xl font-bold text-gray-900 dark:text-white tabular-nums">
                    <AnimatedNumber value={card.value} />
                  </div>
                </div>
                <div className={`w-10 h-10 rounded-xl ${card.accent.bg} flex items-center justify-center`}>
                  <card.icon className={`w-5 h-5 ${card.accent.icon}`} />
                </div>
              </div>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs text-gray-400 mb-1">{card.sub}</p>
                  <TrendBadge value={card.trend} />
                </div>
                <Sparkline data={card.spark} color={card.color} />
              </div>
            </button>
          </motion.div>
        ))}
      </div>

      {/* ROW 2 — Conversation Status + Message Sources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div {...fadeUp(0.24)} className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 h-full">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-gray-900 dark:text-white">Conversation Status</h2>
              <button onClick={() => navigate('/app/conversations')} className="text-xs text-emerald-600 hover:underline font-medium flex items-center gap-1">
                View all <ExternalLink className="w-3 h-3" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              {[
                { label: 'Active',   value: stats.conversations?.active   || 0, pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
                { label: 'Pending',  value: stats.conversations?.pending  || 0, pill: 'bg-amber-50 text-amber-700 border-amber-200',       dot: 'bg-amber-500'   },
                { label: 'Resolved', value: stats.conversations?.resolved || 0, pill: 'bg-blue-50 text-blue-700 border-blue-200',           dot: 'bg-blue-500'    },
                { label: 'Unread',   value: stats.conversations?.unread   || 0, pill: 'bg-rose-50 text-rose-700 border-rose-200',           dot: 'bg-rose-500'    },
              ].map(({ label, value, pill, dot }) => (
                <div key={label} className={`rounded-xl border px-4 py-4 ${pill}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2 h-2 rounded-full ${dot}`} />
                    <span className="text-xs font-medium opacity-70">{label}</span>
                  </div>
                  <div className="text-3xl font-bold tabular-nums">
                    <AnimatedNumber value={value} duration={800} />
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2.5">
              {[
                { label: 'Active',   value: stats.conversations?.active   || 0, total: stats.overview?.total_conversations || 1, color: 'bg-emerald-500' },
                { label: 'Resolved', value: stats.conversations?.resolved || 0, total: stats.overview?.total_conversations || 1, color: 'bg-blue-500'    },
                { label: 'Pending',  value: stats.conversations?.pending  || 0, total: stats.overview?.total_conversations || 1, color: 'bg-amber-500'   },
              ].map(({ label, value, total, color }) => {
                const pct = Math.round((value / total) * 100);
                return (
                  <div key={label} className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-16">{label}</span>
                    <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <motion.div className={`h-full ${color} rounded-full`}
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 1, delay: 0.5 }}
                      />
                    </div>
                    <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <span className="text-xs text-gray-400">Resolution Rate</span>
              <span className="text-sm font-bold text-emerald-600">{resolutionRate}%</span>
            </div>
          </div>
        </motion.div>

        <motion.div {...fadeUp(0.28)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 h-full flex flex-col">
            <h2 className="text-base font-bold text-gray-900 dark:text-white mb-5">Message Sources</h2>
            <div className="flex-1 flex flex-col items-center justify-center gap-4">
              <div className="relative">
                <DonutChart segments={donutData} size={110} />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{stats.messages?.total || 0}</span>
                  <span className="text-[10px] text-gray-400">total</span>
                </div>
              </div>
              <div className="w-full space-y-2">
                {donutData.map(seg => (
                  <div key={seg.label} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: seg.color }} />
                      <span className="text-xs text-gray-600 dark:text-gray-400">{seg.label}</span>
                    </div>
                    <span className="text-xs font-bold text-gray-900 dark:text-white">{seg.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* ROW 3 — Automation Card + Performance + Business Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Automation gradient card */}
        <motion.div {...fadeUp(0.32)}>
          <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-6 h-full flex flex-col relative overflow-hidden shadow-xl shadow-emerald-200/40">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl" />
            <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Automation</p>
                    <p className="text-emerald-100 text-xs">{settings.is_bot_active ? '🟢 Running' : '⭕ Offline'}</p>
                  </div>
                </div>
                <Switch
                  checked={settings.is_bot_active}
                  onCheckedChange={toggleAutomation}
                  data-testid="automation-toggle"
                  className="data-[state=checked]:bg-white/30 data-[state=unchecked]:bg-white/20"
                />
              </div>
              <p className="text-emerald-50 text-sm mb-5 leading-relaxed">
                {settings.is_bot_active
                  ? 'Your bot is actively handling customer queries 24/7 with keyword-based replies.'
                  : 'Enable automation to handle customer queries automatically.'}
              </p>
              {[
                { label: 'Automation Rate', value: `${automationRate}%`, width: automationRate },
                { label: 'Response Rate',   value: `${responseRate}%`,   width: responseRate  },
              ].map(({ label, value, width }) => (
                <div key={label} className="mb-3">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-emerald-100">{label}</span>
                    <span className="text-white font-bold">{value}</span>
                  </div>
                  <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <motion.div className="h-full bg-white rounded-full" initial={{ width: 0 }} animate={{ width: `${width}%` }} transition={{ duration: 1.2, delay: 0.6 }} />
                  </div>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
                <span className="text-emerald-100 text-xs">Bot messages today</span>
                <span className="text-white font-bold text-lg tabular-nums">{stats.messages?.by_sender?.bot || 0}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Performance Metrics */}
        <motion.div {...fadeUp(0.36)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 h-full">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-gray-900 dark:text-white">Performance</h2>
              <Award className="w-4 h-4 text-yellow-500" />
            </div>
            <div className="space-y-3">
              {[
                { label: 'Total Products', value: stats.overview?.total_products || 0, active: stats.overview?.active_products || 0, icon: Package,   color: 'text-violet-600', bg: 'bg-violet-50', path: '/app/products' },
                { label: 'Total FAQs',     value: stats.overview?.total_faqs     || 0, active: stats.overview?.active_faqs     || 0, icon: HelpCircle, color: 'text-amber-600',  bg: 'bg-amber-50',  path: '/app/faqs'     },
                { label: 'Total Leads',    value: stats.overview?.leads           || 0, active: null,                                 icon: Users,      color: 'text-blue-600',   bg: 'bg-blue-50',   path: '/app/leads'    },
              ].map(({ label, value, active, icon: Icon, color, bg, path }) => (
                <button key={label} onClick={() => navigate(path)} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group">
                  <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${color}`} />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{label}</p>
                    {active !== null && <p className="text-xs text-gray-400">{active} active</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{value}</span>
                    <ArrowUpRight className={`w-4 h-4 ${color} opacity-0 group-hover:opacity-100 transition-opacity`} />
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center gap-2 text-xs text-gray-400">
              <Activity className="w-3.5 h-3.5 text-emerald-500" /> Auto-refreshes every 30s
            </div>
          </div>
        </motion.div>

        {/* Business Status */}
        <motion.div {...fadeUp(0.40)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 h-full flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Shield className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">Business Status</h2>
            </div>
            <div className="flex-1 space-y-3">
              {[
                { label: 'WhatsApp API', status: 'Connected', icon: Wifi,  color: 'text-emerald-600', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700' },
                { label: 'Bot', status: settings.is_bot_active ? 'Running' : 'Offline', icon: settings.is_bot_active ? Radio : WifiOff, color: settings.is_bot_active ? 'text-emerald-600' : 'text-gray-400', bg: settings.is_bot_active ? 'bg-emerald-50' : 'bg-gray-50', badge: settings.is_bot_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500' },
              ].map(({ label, status, icon: Icon, color, bg, badge }) => (
                <div key={label} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 ${color}`} />
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge}`}>{status}</span>
                </div>
              ))}
              {settings.business_hours && (
                <div className="flex items-start gap-2.5 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Business Hours</p>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{settings.business_hours}</p>
                  </div>
                </div>
              )}
              {settings.location && (
                <div className="flex items-start gap-2.5 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4 h-4 text-pink-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Location</p>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{settings.location}</p>
                  </div>
                </div>
              )}
              {!settings.business_hours && !settings.location && (
                <p className="text-sm text-gray-400 p-3">Configure your business details in Settings.</p>
              )}
            </div>
            <button onClick={() => navigate('/app/settings')} className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-400 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all">
              Update Settings <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      </div>

      {/* ROW 4 — Weekly Chart + Monthly Goals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div {...fadeUp(0.44)} className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <BarChart2 className="w-4 h-4 text-emerald-500" /> Weekly Activity
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">Conversations, messages & leads this week</p>
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                {[
                  { color: 'bg-emerald-500', label: 'Conversations' },
                  { color: 'bg-blue-500',    label: 'Messages'      },
                  { color: 'bg-violet-500',  label: 'Leads'         },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5 text-gray-500">
                    <span className={`w-2.5 h-2.5 rounded-full ${l.color}`} />
                    {l.label}
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-400 mb-2">Conversations</p>
                <WeeklyBarChart data={weeklyConversations} color="#10b981" />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-2">Messages</p>
                <WeeklyBarChart data={weeklyMessages} color="#3b82f6" />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-2">Leads</p>
                <WeeklyBarChart data={weeklyLeads} color="#8b5cf6" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div {...fadeUp(0.48)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Trophy className="w-4 h-4 text-yellow-500" /> Monthly Goals
              </h2>
              <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                {now.toLocaleDateString('en-IN', { month: 'long' })}
              </span>
            </div>
            <div className="flex-1 flex items-center justify-around">
              {goals.map(g => (
                <GoalRing key={g.label} value={g.value} max={g.max} color={g.color} size={72} label={g.label} sublabel={g.sublabel} />
              ))}
            </div>
            <p className="text-xs text-gray-400 text-center mt-4">
              Goals are based on industry benchmarks. Adjust in Settings.
            </p>
          </div>
        </motion.div>
      </div>

      {/* ROW 5 — Bot Health Score + Setup Checklist */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div {...fadeUp(0.52)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Flash className="w-4 h-4 text-emerald-500" /> Bot Health Score
              </h2>
              <span className="text-[10px] text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full font-medium">Live</span>
            </div>
            <BotHealthScore score={botHealthScore} />
            <div className="mt-5 space-y-2.5">
              {[
                { label: 'Automation Rate',  value: Number(automationRate),                                         max: 100, color: 'bg-emerald-500', weight: '35%' },
                { label: 'FAQ Coverage',     value: Math.min((stats.overview?.total_faqs || 0) / 20, 1) * 100,     max: 100, color: 'bg-amber-500',   weight: '25%' },
                { label: 'Product Catalog',  value: Math.min((stats.overview?.total_products || 0) / 10, 1) * 100, max: 100, color: 'bg-violet-500',  weight: '20%' },
                { label: 'Bot Active',       value: settings.is_bot_active ? 100 : 0,                               max: 100, color: 'bg-blue-500',    weight: '20%' },
              ].map(({ label, value, color, weight }) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-32 flex-shrink-0">{label}</span>
                  <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div className={`h-full ${color} rounded-full`}
                      initial={{ width: 0 }}
                      animate={{ width: `${value}%` }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 w-8 text-right">{Math.round(value)}%</span>
                  <span className="text-[10px] text-gray-300 w-8">{weight}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div {...fadeUp(0.56)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-emerald-500" /> Setup Checklist
              </h2>
              <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                {setupDone}/{setupChecklist.length} done
              </span>
            </div>
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-gray-400">Overall progress</span>
                <span className="font-semibold text-gray-700 dark:text-gray-300">{Math.round((setupDone / setupChecklist.length) * 100)}%</span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(setupDone / setupChecklist.length) * 100}%` }}
                  transition={{ duration: 1, delay: 0.3 }}
                />
              </div>
            </div>
            <div className="flex-1 space-y-0.5">
              {setupChecklist.map((item, i) => (
                <CheckItem key={i} {...item} navigate={navigate} />
              ))}
            </div>
            {setupDone === setupChecklist.length && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800 text-center">
                <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">🎉 Setup complete! Your bot is fully configured.</p>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>

      {/* ROW 6 — Top Performers + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div {...fadeUp(0.60)} className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" /> Top Performers
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { title: 'Top Products', items: stats.top_performing?.top_products?.slice(0, 4).map(p => ({ label: p.name, value: `${p.order_count || 0} orders`, bar: p.order_count || 0 })) || [], empty: 'No product data yet', link: '/app/products',   icon: Package,   color: 'text-violet-600', barColor: 'bg-violet-400' },
                { title: 'Top FAQs',     items: stats.top_performing?.most_viewed_faqs?.slice(0, 4).map(f => ({ label: f.question?.slice(0,28) + (f.question?.length > 28 ? '…' : ''), value: `${f.view_count || 0} views`, bar: f.view_count || 0 })) || [], empty: 'No FAQ data yet',     link: '/app/faqs',        icon: HelpCircle, color: 'text-amber-600',  barColor: 'bg-amber-400'  },
                { title: 'Top Keywords', items: stats.top_performing?.most_used_keywords?.slice(0, 4).map(k => ({ label: k.keyword, value: `${k.usage_count || 0} uses`, bar: k.usage_count || 0 })) || [], empty: 'No keyword data',    link: '/app/automation', icon: Zap,       color: 'text-blue-600',   barColor: 'bg-blue-400'   },
              ].map(({ title, items, empty, link, icon: Icon, color, barColor }) => {
                const maxBar = Math.max(...items.map(i => i.bar), 1);
                return (
                  <div key={title} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Icon className={`w-4 h-4 ${color}`} />
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</span>
                      </div>
                      <button onClick={() => navigate(link)} className="text-[11px] text-emerald-600 hover:underline">View all</button>
                    </div>
                    {items.length > 0 ? (
                      <div className="space-y-2.5">
                        {items.map((item, idx) => (
                          <div key={idx}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-600 dark:text-gray-400 truncate flex-1 mr-2">{item.label}</span>
                              <span className="text-xs font-bold text-gray-700 dark:text-gray-300 flex-shrink-0">{item.value}</span>
                            </div>
                            <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                              <motion.div className={`h-full ${barColor} rounded-full`} initial={{ width: 0 }} animate={{ width: `${(item.bar / maxBar) * 100}%` }} transition={{ duration: 0.8, delay: 0.5 + idx * 0.1 }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 py-2">{empty}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        <motion.div {...fadeUp(0.64)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 h-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-500" /> Live Activity
              </h2>
              <LivePulse />
            </div>
            <div className="space-y-0">
              {activityFeed.map((item, i) => (
                <ActivityItem key={i} {...item} index={i} />
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* ROW 7 — Quick Actions */}
      <motion.div {...fadeUp(0.68)}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
          <h2 className="text-base font-bold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500" /> Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { icon: MessageSquare, label: 'View Chats',   sub: `${stats.conversations?.unread || 0} unread`,        path: '/app/conversations', accent: 'hover:border-blue-200 hover:bg-blue-50',       icolor: 'text-blue-600',    ibg: 'bg-blue-50',    testid: 'quick-action-conversations', badge: stats.conversations?.unread > 0 ? stats.conversations.unread : null },
              { icon: Package,       label: 'Products',     sub: `${stats.overview?.total_products || 0} items`,      path: '/app/products',      accent: 'hover:border-violet-200 hover:bg-violet-50',   icolor: 'text-violet-600',  ibg: 'bg-violet-50',  testid: 'quick-action-products',      badge: null },
              { icon: HelpCircle,    label: 'FAQs',         sub: `${stats.overview?.total_faqs || 0} questions`,     path: '/app/faqs',          accent: 'hover:border-amber-200 hover:bg-amber-50',     icolor: 'text-amber-600',   ibg: 'bg-amber-50',   testid: 'quick-action-faqs',          badge: null },
              { icon: BarChart3,     label: 'Analytics',    sub: 'View reports',                                      path: '/app/analytics',     accent: 'hover:border-emerald-200 hover:bg-emerald-50', icolor: 'text-emerald-600', ibg: 'bg-emerald-50', testid: 'quick-action-analytics',     badge: null },
              { icon: CreditCard,    label: 'Subscription', sub: 'Manage your plan',                                  path: '/app/subscription',  accent: 'hover:border-emerald-200 hover:bg-emerald-50', icolor: 'text-emerald-600', ibg: 'bg-emerald-50', testid: 'quick-action-subscription',  badge: null },
            ].map(({ icon: Icon, label, sub, path, accent, icolor, ibg, testid, badge }) => (
              <button key={label} onClick={() => navigate(path)} data-testid={testid}
                className={`group relative flex flex-col items-start gap-3 p-5 rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 ${accent} transition-all duration-200 text-left`}
              >
                {badge != null && (
                  <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
                <div className={`w-10 h-10 rounded-xl ${ibg} flex items-center justify-center transition-colors`}>
                  <Icon className={`w-5 h-5 ${icolor}`} />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-800 dark:text-gray-200">{label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ROW 8 — Tips Banner */}
      <motion.div {...fadeUp(0.72)}>
        <div className="relative bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 overflow-hidden">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, rgba(16,185,129,0.15) 0%, transparent 60%)' }} />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-bold text-base">Boost your automation rate</p>
                <p className="text-gray-400 text-sm mt-0.5">Add more FAQs and keywords to make your bot handle more queries automatically</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/app/faqs')} className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold transition-colors">
                Add FAQs
              </button>
              <button onClick={() => navigate('/app/automation')} className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors border border-white/20">
                Setup Keywords
              </button>
            </div>
          </div>
        </div>
      </motion.div>

    </div>
  );
}
